import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, PackageOpen, AlertTriangle, CreditCard, XCircle, ShieldCheck, Flag, RefreshCw, Clock, Truck, CheckCircle2, Wallet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_ICONS = {
  return_requested: PackageOpen,
  seller_dispute: AlertTriangle,
  return_received: RefreshCw,
  payout_ready: CreditCard,
  cancellation_requested: XCircle,
  cancel_refund_pending: XCircle,
  ship_timeout_cancelled: Clock,
  verification_pending: ShieldCheck,
  user_reported: Flag,
  // Info events
  order_paid: Wallet,
  order_shipped: Truck,
  order_delivered: CheckCircle2,
};

const EVENT_COLORS = {
  return_requested: 'text-orange-600',
  seller_dispute: 'text-red-600',
  return_received: 'text-green-600',
  payout_ready: 'text-blue-600',
  cancellation_requested: 'text-amber-600',
  cancel_refund_pending: 'text-rose-700',
  ship_timeout_cancelled: 'text-red-700',
  verification_pending: 'text-violet-600',
  user_reported: 'text-rose-600',
  // Info events — muted
  order_paid: 'text-[#A89F91]',
  order_shipped: 'text-[#A89F91]',
  order_delivered: 'text-[#A89F91]',
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function AdminNotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const dropdownRef = useRef(null);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications/summary`);
      setSummary(res.data);
    } catch (err) {
      // Keep previous summary on transient failure — don't blank the dropdown.
      // Log so live issues can be diagnosed from the browser console.
      console.warn('[AdminNotificationsBell] fetchSummary failed:', err?.response?.status, err?.response?.data || err?.message);
    }
  }, []);

  // Poll every 20s while tab is visible + refetch immediately on tab focus
  // / visibility change. Browsers aggressively throttle setInterval on
  // backgrounded tabs (down to once per minute or pausing entirely) — so a
  // pure interval-only approach leaves the admin staring at stale data.
  useEffect(() => {
    fetchSummary();
    const id = setInterval(fetchSummary, 20000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchSummary(); };
    const onFocus = () => fetchSummary();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchSummary]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      // Opening: mark seen
      try {
        await axios.post(`${API}/admin/notifications/mark-seen`);
        fetchSummary();
      } catch { /* ignore */ }
    }
  };

  const handleItemClick = (item) => {
    setOpen(false);
    navigate(`/admin#${item.tab}`);
  };

  const unseen = summary?.new_since_seen || 0;
  const total = summary?.total_actionable || 0;
  const feed = summary?.feed || [];
  const infoFeed = summary?.info_feed || [];
  const newInfo = summary?.new_info_since_seen || 0;
  // Badge reflects outstanding work (total_actionable) so it doesn't disappear
  // after a single bell-open. Fresh items are still distinguished via the red
  // "NEW" pill on individual rows. When there are unseen new items we add a
  // gentle pulse ring so the admin can tell "something changed" vs "same queue".
  const hasWork = total > 0;
  const hasNew = unseen > 0 || newInfo > 0;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className="relative text-[#78716C] hover:text-[#2C2A29] transition-colors"
        title="Admin notifications"
        data-testid="admin-notifications-bell"
      >
        <Bell size={20} />
        {hasWork && (
          <span
            className={`absolute -top-2 -right-2 text-white text-[10px] font-bold min-w-[20px] h-5 px-1 rounded-full flex items-center justify-center ${
              hasNew ? 'bg-red-500 ring-2 ring-red-200 animate-pulse' : 'bg-[#C5A880]'
            }`}
            data-testid="admin-notifications-unseen-badge"
            title={hasNew ? `${unseen} new · ${total} total pending` : `${total} pending`}
          >
            {total > 9 ? '9+' : total}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl border border-cream-100 shadow-[0_10px_40px_rgb(0,0,0,0.08)] overflow-hidden z-50"
          data-testid="admin-notifications-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-cream-100/80">
            <div>
              <p className="font-heading text-base text-[#2C2A29]">Admin actions</p>
              <p className="text-xs font-body text-[#A89F91]">
                {total === 0 ? 'Nothing pending 🎉' : `${total} item${total === 1 ? '' : 's'} awaiting action`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/admin/notifications/mark-seen`);
                    fetchSummary();
                  } catch { /* ignore */ }
                }}
                className="text-xs font-body text-[#6B1F2B] hover:underline disabled:opacity-40 disabled:no-underline"
                data-testid="admin-notifications-mark-seen"
                disabled={unseen === 0 && newInfo === 0}
                title={unseen === 0 && newInfo === 0 ? 'Nothing new to mark' : 'Clear the NEW highlights below'}
              >
                Mark all as seen
              </button>
              <button
                onClick={fetchSummary}
                className="text-[#78716C] hover:text-[#2C2A29] transition-colors"
                title="Refresh"
                data-testid="admin-notifications-refresh"
              >
                <RefreshCw size={14} />
              </button>
            </div>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {feed.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm font-body text-[#A89F91]" data-testid="admin-notifications-empty">
                You&apos;re all caught up on actions!
              </div>
            ) : (
              feed.map((item, i) => {
                const Icon = EVENT_ICONS[item.event_type] || Bell;
                const color = EVENT_COLORS[item.event_type] || 'text-[#78716C]';
                const isNew = summary?.last_seen_at ? (item.created_at && item.created_at > summary.last_seen_at) : true;
                return (
                  <button
                    key={`${item.event_type}-${item.ref_id}-${i}`}
                    onClick={() => handleItemClick(item)}
                    className="w-full flex items-start gap-3 px-4 py-3 border-b border-cream-100/50 hover:bg-cream-50 transition-colors text-left"
                    data-testid={`admin-notif-item-${item.event_type}-${item.ref_id}`}
                  >
                    <div className={`mt-0.5 flex-shrink-0 ${color}`}>
                      <Icon size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-body text-[#2C2A29] leading-snug">
                        {item.title}
                        {isNew && <span className="ml-2 inline-block bg-red-100 text-red-700 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" data-testid="admin-notif-new-badge">New</span>}
                      </p>
                      <p className="text-xs font-body text-[#A89F91] mt-0.5">{timeAgo(item.created_at)}</p>
                    </div>
                  </button>
                );
              })
            )}

            {/* Recent activity (informational, no action required) */}
            {infoFeed.length > 0 && (
              <>
                <div className="px-4 pt-3 pb-1.5 bg-cream-50/40 border-t border-cream-100/80">
                  <p className="text-[10px] font-body font-semibold uppercase tracking-wider text-[#A89F91]">Recent activity</p>
                </div>
                {infoFeed.map((item, i) => {
                  const Icon = EVENT_ICONS[item.event_type] || Bell;
                  const color = EVENT_COLORS[item.event_type] || 'text-[#A89F91]';
                  const isNew = summary?.last_seen_at ? (item.created_at && item.created_at > summary.last_seen_at) : true;
                  return (
                    <button
                      key={`info-${item.event_type}-${item.ref_id}-${i}`}
                      onClick={() => handleItemClick(item)}
                      className="w-full flex items-start gap-3 px-4 py-2.5 border-b border-cream-100/40 hover:bg-cream-50 transition-colors text-left bg-cream-50/20"
                      data-testid={`admin-notif-info-${item.event_type}-${item.ref_id}`}
                    >
                      <div className={`mt-0.5 flex-shrink-0 ${color}`}>
                        <Icon size={14} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-body text-[#78716C] leading-snug">
                          {item.title}
                          {isNew && <span className="ml-2 inline-block bg-cream-100 text-[#6B1F2B] text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded">New</span>}
                        </p>
                        <p className="text-xs font-body text-[#A89F91] mt-0.5">{timeAgo(item.created_at)}</p>
                      </div>
                    </button>
                  );
                })}
              </>
            )}
          </div>

          <button
            onClick={() => { setOpen(false); navigate('/admin'); }}
            className="w-full py-3 text-xs font-body font-semibold text-[#6B1F2B] hover:bg-cream-50 transition-colors border-t border-cream-100/80"
            data-testid="admin-notifications-open-panel"
          >
            Open Admin Panel →
          </button>
        </div>
      )}
    </div>
  );
}
